import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { claudeTokens } from '@/styles/claudeTokens';

interface PageHeaderProps {
    className?: string;
    title: string;
    subtitle?: string;
    backLabel?: string;
    backTo?: string | number;
    onBack?: () => void;
    actions?: ReactNode;
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 24px;

    @media (max-width: 768px) {
        margin-bottom: 16px;
    }
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: #6b7280;
    font-size: 14px;
    cursor: pointer;
    padding: 8px 0;
    margin-bottom: 8px;
    align-self: flex-start;

    &:hover { color: #374151; }
    svg { font-size: 20px; }
`;

const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    flex-wrap: wrap;
`;

const TitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Title = styled.h1`
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;

    @media (max-width: 768px) {
        font-size: 22px;
    }
`;

const Subtitle = styled.p`
    font-size: 14px;
    color: ${claudeTokens.color.text2};
    margin: 0;
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
`;

/**
 * 페이지 상단 헤더 공용 organism.
 * - 뒤로가기 버튼(선택), 타이틀, 서브타이틀, 우측 액션 버튼 슬롯
 * - 페이지마다 따로 정의하던 Header/Title/PageTitle/SectionTitle 패턴 통합용
 */
const PageHeader = ({
    className,
    title,
    subtitle,
    backLabel,
    backTo,
    onBack,
    actions,
}: PageHeaderProps) => {
    const navigate = useNavigate();

    const handleBack = () => {
        if (onBack) onBack();
        else if (typeof backTo === 'string') navigate(backTo);
        else navigate(typeof backTo === 'number' ? backTo : -1);
    };

    const showBack = backLabel !== undefined || backTo !== undefined || onBack !== undefined;

    return (
        <Wrapper className={className}>
            {showBack && (
                <BackButton onClick={handleBack}>
                    <ArrowBackIcon />
                    {backLabel ?? '뒤로'}
                </BackButton>
            )}
            <TitleRow>
                <TitleGroup>
                    <Title>{title}</Title>
                    {subtitle && <Subtitle>{subtitle}</Subtitle>}
                </TitleGroup>
                {actions && <Actions>{actions}</Actions>}
            </TitleRow>
        </Wrapper>
    );
};

export default PageHeader;
